import { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import { ParallaxProvider } from 'react-scroll-parallax';

import Footer from 'react-scale/components/elements/footer';
import Header from 'react-scale/components/elements/header';
import Navigation from 'react-scale/components/elements/navigation';
import Hero from 'react-scale/components/pages/careers/Hero';
import About from 'react-scale/components/pages/careers/About';
import Credos from 'react-scale/components/pages/careers/Credos';
import Team from 'react-scale/components/pages/careers/Team';
import Benefits from 'react-scale/components/pages/careers/Benefits';
import Jobs from 'react-scale/components/pages/careers/Jobs';
import {
  navigationLinks,
  aboutTitle,
  valuesTitle,
  valuesSlider,
  teamTitle,
  benefitsTitle
} from 'react-scale/components/pages/careers/Content';

import { fetchData } from 'react-scale/pages/api/careers/jobs-call';

const JobBoardPage = () => {
  const router = useRouter();
  const { query } = router;

  const [jobListings, setJobListings] = useState([]);
  const [offices, setOffices] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [locationFilter, setLocationFilter] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [filteredJobListings, setFilteredJobListings] = useState([]);

  useEffect(() => {
    setLocationFilter(query.location || '');
    setDepartmentFilter(query.department || '');
  }, [query]);

  useEffect(() => {
    async function fetchJobData() {
      try {
        const data = await fetchData();
        setJobListings(data.jobListings);
        setOffices(data.offices);
        setDepartments(data.departments);
      } catch (error) {
        // Handle error if needed
      }
    }

    fetchJobData();
  }, [locationFilter, departmentFilter]);

  const handleLocationFilterChange = e => {
    const location = e.target.value;
    setLocationFilter(location);
    router.push({
      pathname: router.pathname,
      query: { ...query, location },
      hash: 'jobs'
    });
  };

  const handleDepartmentFilterChange = e => {
    const department = e.target.value;
    setDepartmentFilter(department);
    setLocationFilter('');
    router.push({
      pathname: router.pathname,
      query: { ...query, department, location: '' },
      hash: 'jobs'
    });
  };

  useEffect(() => {
    const filteredListings = jobListings.filter(listing => {
      const locationMatches = locationFilter
        ? listing.offices &&
          listing.offices.some(office =>
            office.name.toLowerCase().includes(locationFilter.toLowerCase())
          )
        : true;
      const departmentMatches = departmentFilter
        ? listing.departments &&
          listing.departments.some(department =>
            department.name.toLowerCase().includes(departmentFilter.toLowerCase())
          )
        : true;

      return locationMatches && departmentMatches;
    });
    setFilteredJobListings(filteredListings);
  }, [locationFilter, departmentFilter, jobListings]);

  // Filter jobless offices & departments
  const departmentsClean = departments.filter(department =>
    jobListings.find(job =>
      job.departments.find(jobDepartment => jobDepartment.id === department.id)
    )
  );

  const officesClean = offices.filter(office =>
    filteredJobListings.find(job => job.offices.find(jobOffice => jobOffice.id === office.id))
  );

  // Render job listings on the page
  return (
    <Jobs
      departments={departmentsClean}
      offices={officesClean}
      jobs={filteredJobListings}
      selectedDepartment={departmentFilter}
      handleDepartmentChange={handleDepartmentFilterChange}
      selectedOffice={locationFilter}
      handleOfficeChange={handleLocationFilterChange}
      departmentFilter={departmentFilter}
    />
  );
};

const Careers = () => {
  return (
    <ParallaxProvider>
      <Header
        title="Careers | Scale AI"
        description="Join Scale AI! The transition from traditional software to AI is one of the most important shifts of our time. Scale AI is growing, grow with us!"
        canonical="https://scale.com/careers"
        ogImg="https://scale.com/static/img/website/careers/fb-card.jpg"
        theme="light"
        border
      />
      <Navigation title="Careers" links={navigationLinks.links} theme="light" />
      <Hero />
      <About aboutSection={aboutTitle} canAutoplay={false} />
      <Credos valuesTitle={valuesTitle} valuesSlider={valuesSlider} />
      <Team teamTitle={teamTitle} />
      <Benefits benefitsTitle={benefitsTitle} />
      <JobBoardPage />
      <Footer theme="light" border />
    </ParallaxProvider>
  );
};

Careers.theme = 'light';

export default Careers;
